import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedDate, FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { CopyToClipboardButton } from '#src/atoms/buttons/CopyToClipboardButton/index.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { BodyS, LabelS } from '#src/atoms/typography/index.tsx';
import { ConfirmDialog } from '#src/components/form/ConfirmDialog/index.tsx';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useToggle } from '#src/hooks/useToggle.ts';

import type {
  ApiKeyRow_apiKey,
  DeleteApiKeyMutation,
  DeleteApiKeyMutationVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  rateLimit: {
    defaultMessage: '{count, number} requests/minute',
  },
  createdOn: {
    defaultMessage: 'Created on {date}',
  },
  deleteConfirm: {
    defaultMessage:
      'Any application using this API key will lose its higher rate limit. This cannot be undone.',
  },
});

const fragment = gql`
  fragment ApiKeyRow_apiKey on ApiKey {
    id
    token
    rateLimitPerMinute
    createdAt
  }
` as TypedDocumentNode<ApiKeyRow_apiKey>;

const DELETE_API_KEY_MUTATION = gql`
  mutation DeleteApiKeyMutation($input: deleteApiKeyInput!) {
    deleteApiKey(input: $input) {
      currentUser {
        id
        slug
        apiKeys {
          id
          ...ApiKeyRow_apiKey
        }
      }
      errors {
        message
      }
    }
  }
  ${fragment}
` as TypedDocumentNode<DeleteApiKeyMutation, DeleteApiKeyMutationVariables>;

const Root = styled(SBHorizontal).attrs({ gap: 2 })`
  padding: var(--double-unit) 0;
  &:not(:last-child) {
    border-bottom: 1px solid var(--c-nd-100);
  }
`;

const Token = styled(BodyS)`
  font-family: monospace;
  max-width: 28ch;
`;

type Props = {
  apiKey: ApiKeyRow_apiKey;
};

export const ApiKeyRow = ({ apiKey }: Props) => {
  const [showConfirm, toggleConfirm] = useToggle(false);
  const [deleteApiKey, { loading }] = useMutation(DELETE_API_KEY_MUTATION);

  return (
    <Root>
      <Vertical gap={0.5}>
        <Horizontal gap={1}>
          <Token title={apiKey.token} className="truncate">
            {apiKey.token}
          </Token>
          <CopyToClipboardButton textToCopy={apiKey.token} />
        </Horizontal>
        <LabelS color="var(--c-nd-600)">
          <FormattedMessage
            {...messages.rateLimit}
            values={{ count: apiKey.rateLimitPerMinute }}
          />
          {' · '}
          <FormattedMessage
            {...messages.createdOn}
            values={{
              date: (
                <FormattedDate value={apiKey.createdAt} dateStyle="medium" />
              ),
            }}
          />
        </LabelS>
      </Vertical>
      <Button
        size="small"
        color="destructive"
        loading={loading}
        onClick={() => toggleConfirm()}
      >
        <FormattedMessage defaultMessage="Delete" />
      </Button>
      <ConfirmDialog
        open={showConfirm}
        onClose={() => toggleConfirm()}
        message={
          <BodyS className="text-center">
            <FormattedMessage {...messages.deleteConfirm} />
          </BodyS>
        }
        onConfirm={async () => {
          await deleteApiKey({ variables: { input: { id: apiKey.id } } });
        }}
        ctaProps={{ color: 'destructive' }}
      />
    </Root>
  );
};

ApiKeyRow.fragments = {
  apiKey: fragment,
};
